import { UserRoles } from '../../api/queries';
import i18n from '../../i18n/config';

export type RoleInfo = {
  key: UserRoles;
  label: string;
  description: string;
};

export class RolesUtils {
  /**
   * Get all roles that can be administered (excludes SUPER_ADMIN)
   */
  public static getAdministerableRoles(): RoleInfo[] {
    return [
      {
        key: UserRoles.ORG_ADMIN,
        label: i18n.t('roles.ORG_ADMIN.label'),
        description: i18n.t('roles.ORG_ADMIN.description'),
      },
      {
        key: UserRoles.HR_ADMIN,
        label: i18n.t('roles.HR_ADMIN.label'),
        description: i18n.t('roles.HR_ADMIN.description'),
      },
      {
        key: UserRoles.TEAM_LEAD,
        label: i18n.t('roles.TEAM_LEAD.label'),
        description: i18n.t('roles.TEAM_LEAD.description'),
      },
      {
        key: UserRoles.CONTENT_CREATOR,
        label: i18n.t('roles.CONTENT_CREATOR.label'),
        description: i18n.t('roles.CONTENT_CREATOR.description'),
      },
      {
        key: UserRoles.SAFETY_REP,
        label: i18n.t('roles.SAFETY_REP.label'),
        description: i18n.t('roles.SAFETY_REP.description'),
      },
      {
        key: UserRoles.GENERAL_MANAGER,
        label: i18n.t('roles.GENERAL_MANAGER.label'),
        description: i18n.t('roles.GENERAL_MANAGER.description'),
      },
      {
        key: UserRoles.MEMBER,
        label: i18n.t('roles.MEMBER.label'),
        description: i18n.t('roles.MEMBER.description'),
      },
    ];
  }

  /**
   * Get human-readable label for a role
   */
  public static getRoleLabel(role: string): string {
    const roleInfo = this.getAdministerableRoles().find((r) => r.key === role);
    return roleInfo?.label ?? role;
  }

  /**
   * Get multiple role labels
   */
  public static getRoleLabels(roles: string[]): string[] {
    return roles
      .filter((role) => role !== UserRoles.SUPER_ADMIN)
      .map((role) => this.getRoleLabel(role));
  }

  /**
   * Check if user has at least one admin role (ORG_ADMIN or HR_ADMIN)
   */
  public static hasAdminRole(roles: string[]): boolean {
    return (
      roles.includes(UserRoles.ORG_ADMIN) ||
      roles.includes(UserRoles.HR_ADMIN)
    );
  }

  /**
   * Check if user is organization admin
   */
  public static isOrgAdmin(roles: string[]): boolean {
    return roles.includes(UserRoles.ORG_ADMIN);
  }

  /**
   * Filter out SUPER_ADMIN from roles list
   */
  public static filterAdministerableRoles(roles: string[]): string[] {
    return roles.filter((role) => role !== UserRoles.SUPER_ADMIN);
  }

  /**
   * Get roles that can be assigned during invitation/editing
   * Excludes TEAM_LEAD (assigned via team membership) and MEMBER (default role)
   */
  public static getInvitableRoles(): RoleInfo[] {
    return this.getAdministerableRoles().filter(
      (role) => role.key !== UserRoles.TEAM_LEAD && role.key !== UserRoles.MEMBER
    );
  }
}
